/**
 * NewMeetingAttendeesField — attendee management for the New Meeting form.
 *
 * Distinct from the editor's AttendeesPanel (which manages speaker
 * groups inside the structured document). This is the pre-creation
 * field where users curate the attendee list BEFORE the draft exists,
 * either pre-filled from a calendar event or added manually.
 *
 * Two slots:
 *   - OJB attendees — typeahead against the roster (OjbMemberPicker).
 *     Selected members carry their roster info (title, office,
 *     headshot, AND email). Email always present for OJB.
 *   - External attendees — name-only inline add. No email required;
 *     the api treats email as optional. Externals without email are
 *     visible attendees / speakers but get filtered out of email-send
 *     and follow-up-invite recipient lists downstream.
 *
 * All entries serialize to the AttendeeEmail shape the api expects:
 * { name, email?, isOjb }.
 *
 * Identity / dedupe key: email when present, otherwise lowercase name.
 * The `entryKey()` helper is the one source of truth — used for
 * React keys, dedupe checks, edit-target lookup, and remove.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Plus, X } from 'lucide-react';
import { OjbMemberPicker } from './OjbMemberPicker.tsx';
import { AttendeeAvatar } from './AttendeeAvatar.tsx';
import { useOjbTeamRoster } from '../../hooks/useOjbTeamRoster.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';

export interface AttendeeEmailEntry {
  name: string;
  /** Optional — externals can be added by name only. Always present
   *  for OJB attendees because the picker pulls from the roster. */
  email?: string;
  isOjb: boolean;
}

interface Props {
  value: AttendeeEmailEntry[];
  onChange: (next: AttendeeEmailEntry[]) => void;
}

/** Dedupe + React-key derivation. Email is the strong identifier
 *  when present; falls back to a lowercase-name key for externals
 *  added without an email. Two externals with the same name + no
 *  email collapse to one entry — pilot-acceptable edge case. */
function entryKey(a: AttendeeEmailEntry): string {
  if (a.email && a.email.trim().length > 0) {
    return `email:${a.email.toLowerCase()}`;
  }
  return `name:${a.name.toLowerCase().trim()}`;
}

export function NewMeetingAttendeesField({ value, onChange }: Props) {
  const { lookupPhoto, members: roster } = useOjbTeamRoster();
  const [externalName, setExternalName] = useState('');
  const [externalError, setExternalError] = useState<string | null>(null);
  // Key of the external pill currently being inline-edited (see
  // entryKey). null means no edit in progress.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const editInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus + select-all when entering edit mode so the user can
  // type the replacement directly without an extra click.
  useEffect(() => {
    if (editingKey && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingKey]);

  // Backfill: when the roster query lands AFTER calendar prefill
  // already populated some OJB attendees with their Outlook
  // displayName (no credentials), rewrite any mismatched names to
  // the roster's authoritative fullName. Idempotent — no-op when
  // names already match, so the effect doesn't loop.
  useEffect(() => {
    if (roster.length === 0) return;
    let changed = false;
    const next = value.map((a) => {
      if (!a.isOjb || !a.email) return a;
      const m = roster.find(
        (r) => r.email && r.email.toLowerCase() === a.email!.toLowerCase(),
      );
      if (m?.fullName && m.fullName !== a.name) {
        changed = true;
        return { ...a, name: m.fullName };
      }
      return a;
    });
    if (changed) onChange(next);
  }, [roster, value, onChange]);

  const ojbAttendees = value.filter((a) => a.isOjb);
  const externalAttendees = value.filter((a) => !a.isOjb);
  const ojbNames = ojbAttendees.map((a) => a.name);

  function startEdit(key: string, currentName: string) {
    setEditingKey(key);
    setEditingValue(currentName);
  }

  function commitEdit() {
    if (!editingKey) return;
    const trimmed = editingValue.trim();
    if (trimmed.length === 0) {
      // Empty name — cancel rather than save a blank pill.
      setEditingKey(null);
      return;
    }
    onChange(
      value.map((a) =>
        entryKey(a) === editingKey ? { ...a, name: trimmed } : a,
      ),
    );
    setEditingKey(null);
  }

  function cancelEdit() {
    setEditingKey(null);
    setEditingValue('');
  }

  function onEditKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  }

  function addOjb(name: string, member: { email?: string | null } | null) {
    let email = member?.email ?? null;
    if (!email) {
      const match = roster.find(
        (r) => r.fullName.toLowerCase() === name.toLowerCase(),
      );
      email = match?.email ?? null;
    }
    if (!email) {
      // OJB attendees REQUIRE an email — without it we can't carry
      // them through the email-send flow. Silently skip rather than
      // miscategorize as External.
      return;
    }
    const newKey = `email:${email.toLowerCase()}`;
    if (value.some((a) => entryKey(a) === newKey)) return;
    onChange([...value, { name, email, isOjb: true }]);
  }

  function addExternal() {
    const name = externalName.trim();
    if (!name) {
      setExternalError('Name required');
      return;
    }
    const newKey = `name:${name.toLowerCase()}`;
    if (value.some((a) => entryKey(a) === newKey)) {
      setExternalError('Already added');
      return;
    }
    onChange([...value, { name, isOjb: false }]);
    setExternalName('');
    setExternalError(null);
  }

  // Enter-as-add for the external add input. CRITICAL: we MUST
  // preventDefault here because this whole field lives inside the
  // outer NewMeetingRoute <form>, and a stray Enter would otherwise
  // submit the outer form (creating a half-baked draft and yeeting
  // the user to the editor mid-flow).
  function onAddInputKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addExternal();
    }
  }

  function remove(key: string) {
    onChange(value.filter((a) => entryKey(a) !== key));
  }

  return (
    <div className="space-y-4">
      {/* ─── OJB attendees ───────────────────────────────────────── */}
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-pine">
          OJB attendees
          {ojbAttendees.length > 0 && (
            <span className="ml-1.5 text-charcoal/50">
              ({ojbAttendees.length})
            </span>
          )}
        </div>

        {ojbAttendees.length > 0 && (
          <ul className="mb-2 flex flex-wrap gap-2">
            {ojbAttendees.map((a) => {
              // Defensive re-merge with roster at render time — covers
              // the race where calendar prefill ran before the roster
              // query resolved (in which case the stored name is the
              // Outlook displayName without credentials). Match by
              // email; fall back to whatever's stored in `a.name`.
              const rosterMatch = a.email
                ? roster.find(
                    (r) => r.email && r.email.toLowerCase() === a.email!.toLowerCase(),
                  )
                : undefined;
              const displayName = rosterMatch?.fullName ?? a.name;
              const k = entryKey(a);
              return (
                <li
                  key={k}
                  className="group flex items-center gap-2 rounded-full border border-pine/25 bg-pine/5 py-1 pl-1 pr-2 text-xs text-charcoal"
                >
                  <AttendeeAvatar
                    name={displayName}
                    headshotUrl={lookupPhoto(displayName)}
                    size={22}
                  />
                  {/* Single-line: name (which already includes any
                      professional credentials per the OJB roster
                      convention "First Last, AIA"). Title + office
                      are intentionally omitted for visual consistency
                      across pills. */}
                  <span className="font-medium">{displayName}</span>
                  <button
                    type="button"
                    onClick={() => remove(k)}
                    aria-label={`Remove ${displayName}`}
                    title="Remove"
                    className="ml-1 rounded-full p-0.5 text-pine/55 transition-colors hover:bg-pine/10 hover:text-pine"
                  >
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <OjbMemberPicker
          onSelect={(name, member) => addOjb(name, member)}
          excludeNames={ojbNames}
          placeholder="Add OJB staff by name…"
        />
      </div>

      {/* ─── External attendees ─────────────────────────────────── */}
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-pine">
          External attendees
          {externalAttendees.length > 0 && (
            <span className="ml-1.5 text-charcoal/50">
              ({externalAttendees.length})
            </span>
          )}
        </div>

        {externalAttendees.length > 0 && (
          <ul className="mb-2 flex flex-wrap gap-2">
            {externalAttendees.map((a) => {
              const k = entryKey(a);
              const isEditing = editingKey === k;
              return (
                <li
                  key={k}
                  className="group flex items-center gap-1.5 rounded-full border border-charcoal/20 bg-paper-light py-1 pl-3 pr-2 text-xs text-charcoal"
                >
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={onEditKey}
                      className="rounded border border-pine/40 bg-white px-1.5 py-0 text-xs text-charcoal focus:border-pine focus:outline-none focus:ring-1 focus:ring-pine/30"
                      style={{ width: `${Math.max(8, editingValue.length + 1)}ch` }}
                    />
                  ) : (
                    // Click name to edit. No email shown — externals are
                    // identified by name only (email is optional and
                    // hidden when set).
                    <button
                      type="button"
                      onClick={() => startEdit(k, a.name)}
                      title="Click to edit name"
                      className="rounded font-medium text-charcoal transition-colors hover:bg-charcoal/5 px-1 -mx-1"
                    >
                      {a.name}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(k)}
                    aria-label={`Remove ${a.name}`}
                    title="Remove"
                    className="ml-0.5 rounded-full p-0.5 text-charcoal/45 transition-colors hover:bg-charcoal/10 hover:text-charcoal"
                  >
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* NOT a <form> — see onAddInputKey for the rationale. */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          <input
            {...NO_AUTOFILL}
            type="text"
            value={externalName}
            onChange={(e) => {
              setExternalName(e.target.value);
              setExternalError(null);
            }}
            onKeyDown={onAddInputKey}
            placeholder="Add external attendee by name…"
            className="rounded-lg border border-charcoal/20 bg-paper-light px-2.5 py-1.5 text-xs text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
          />
          <button
            type="button"
            onClick={addExternal}
            disabled={!externalName.trim()}
            className="inline-flex items-center justify-center gap-1 rounded-lg border border-pine/30 bg-pine/10 px-3 py-1.5 text-xs font-semibold text-pine transition-colors hover:bg-pine/15 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={12} strokeWidth={2.5} /> Add
          </button>
        </div>
        {externalError && (
          <div className="mt-1 text-[11px] text-red-700">{externalError}</div>
        )}
      </div>

      {value.length === 0 && (
        <p className="text-[11px] italic text-charcoal/55">
          Add OJB staff by name (typeahead pulls from your roster) or
          add external attendees by name. Calendar prefill captures
          everyone automatically.
        </p>
      )}
    </div>
  );
}
